// Global Instructions Rule Applied!
//
// Deal Brief card — the deal Dashboard's evaluation write-up, derived from the
// deal follower's memory per (deal, section); cfg.deal_brief_sections drives
// the section set/order. Read-only by design, the CompanyBriefCard's sibling.
// Corrections flow through memory ops (company-side today); a deal brief is
// re-materialized by the next deep/zoom run.
import React, { useCallback, useEffect, useState } from 'react'
import { Tag } from 'antd'
import dayjs from 'dayjs'
import { FormSection } from '../../../core/components/FormSection.tsx'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import { fetchDealBrief } from '../services/internalAgentsService.ts'
import { DealBriefSection } from '../../../state_manager/data_models/internal_agents_model.ts'

interface DealBriefCardProps {
  dealId: string
  /** Hands `load` to the deal form so its Refresh button reloads this panel.
   *  Same contract DocumentsTab exposes via onRefreshReady. */
  onRefreshReady?: ((refreshFn: () => Promise<void>) => void) | null
  /** Dashboard placement renders nothing until the follower has written a
   *  brief, so a deal with no agent output keeps today's layout. */
  hideWhenEmpty?: boolean
}

export const DealBriefCard: React.FC<DealBriefCardProps> = ({
  dealId,
  onRefreshReady = null,
  hideWhenEmpty = false
}) => {
  const [sections, setSections] = useState<DealBriefSection[]>([])
  const [loading, setLoading] = useState(true)
  const token = () => getAccessToken(store.getState().auth)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setSections(await fetchDealBrief(dealId, token()))
    } catch (error) {
      console.error('[internal-agents] deal brief load failed:', error)
      setSections([])
    } finally {
      setLoading(false)
    }
  }, [dealId])

  useEffect(() => {
    load()
  }, [load])

  // The app shell hides tabs with display:none rather than unmounting them, so
  // a mount-time-only fetch goes stale. `onRefreshReady` hands `load` up to the
  // deal form so its Refresh button reloads this panel too.
  useEffect(() => {
    if (onRefreshReady) onRefreshReady(load)
  }, [onRefreshReady, load])

  const written = sections.filter((s) => s.deal_briefs__content)
  const generatedAt = written.reduce<string | null>(
    (latest, s) =>
      s.deal_briefs__generated_at && (!latest || s.deal_briefs__generated_at > latest)
        ? s.deal_briefs__generated_at
        : latest,
    null
  )

  if (hideWhenEmpty && !loading && written.length === 0) {
    return null
  }

  return (
    <FormSection
      data-component='DealBriefCard'
      title='Deal Brief'
      titleColor='green'
      loading={loading}
      className='shadow-sm border border-gray-200'
      headerActions={
        <span className='flex items-center gap-2'>
          <Tag color='blue'>Agent-written</Tag>
          {generatedAt ? (
            <span className='text-gray-400 text-[11.5px]'>
              updated {dayjs(generatedAt).format('MM/DD/YYYY')}
            </span>
          ) : null}
        </span>
      }
    >
      {written.length === 0 && !loading ? (
        <div className='text-gray-400 text-xs py-2'>Awaiting first deep run.</div>
      ) : (
        <div className='flex flex-col divide-y divide-gray-100 -mx-1'>
          {sections.map((section) => (
            <div key={section.deal_brief_sections__section_key} className='px-1 py-2'>
              <span className='text-[11.5px] font-semibold uppercase tracking-wide text-gray-500'>
                {section.deal_brief_sections__label}
              </span>
              {section.deal_briefs__content ? (
                <div className='whitespace-pre-line text-[13px] mt-0.5'>
                  {section.deal_briefs__content}
                </div>
              ) : (
                <div className='text-gray-400 text-xs mt-0.5'>Awaiting first deep run.</div>
              )}
            </div>
          ))}
        </div>
      )}
    </FormSection>
  )
}
